<script lang="ts">
  let {
    type = 'text',
    value = $bindable(''),
    placeholder,
    disabled = false,
    invalid = false,
    size = 'md',
    label,
    helper,
    error,
    id = `input-${Math.random().toString(36).slice(2, 9)}`,
    min,
    max,
    maxlength,
    autocomplete,
    spellcheck,
    name,
    required = false,
    oninput,
    onchange,
    onkeydown,
    class: className = ''
  }: {
    type?: 'text' | 'email' | 'password' | 'number' | 'search' | 'url' | 'tel';
    value?: string | number;
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    size?: 'sm' | 'md';
    label?: string;
    helper?: string;
    error?: string;
    id?: string;
    min?: number;
    max?: number;
    maxlength?: number;
    autocomplete?: AutoFill;
    spellcheck?: boolean;
    name?: string;
    required?: boolean;
    oninput?: (e: Event) => void;
    onchange?: (e: Event) => void;
    onkeydown?: (e: KeyboardEvent) => void;
    class?: string;
  } = $props();

  const helperId = $derived(helper ? `${id}-helper` : undefined);
  const errorId = $derived(error ? `${id}-error` : undefined);
  const describedBy = $derived(errorId ?? helperId ?? undefined);
  const isNumber = $derived(type === 'number');
</script>

<div class="field {className}" data-invalid={invalid || undefined} data-disabled={disabled || undefined}>
  {#if label}
    <label class="label" for={id}>{label}</label>
  {/if}
  <input
    {id}
    {type}
    bind:value
    {placeholder}
    {disabled}
    {min}
    {max}
    {maxlength}
    {autocomplete}
    {spellcheck}
    {name}
    required={required || undefined}
    data-size={size}
    data-number={isNumber || undefined}
    aria-invalid={invalid}
    aria-describedby={describedBy}
    {oninput}
    {onchange}
    {onkeydown}
  />
  {#if error}
    <span id={errorId} class="error">{error}</span>
  {:else if helper}
    <span id={helperId} class="helper">{helper}</span>
  {/if}
</div>

<style>
  .field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .label {
    font-size: 0.75rem;
    font-weight: var(--weight-semibold);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  input {
    width: 100%;
    font-family: var(--font-sans);
    color: var(--color-text-primary);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
    caret-color: var(--color-accent);
    transition:
      border-color var(--dur-fast) var(--ease-out),
      background var(--dur-fast) var(--ease-out);
  }

  input::placeholder {
    color: var(--color-text-muted);
    opacity: 1;
  }

  input:hover:not(:disabled) {
    border-color: var(--color-text-faint);
  }

  input:focus-visible {
    border-color: var(--color-accent);
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  input:disabled {
    background: var(--color-surface-sunken);
    color: var(--color-text-muted);
    cursor: not-allowed;
  }

  .field[data-invalid] input {
    border-color: var(--color-danger);
  }

  .field[data-invalid] input:focus-visible {
    outline-color: var(--color-danger);
  }

  input[data-size='sm'] {
    height: 40px;
    padding: 0 var(--space-3);
    font-size: var(--text-sm);
  }

  input[data-size='md'] {
    height: 44px;
    padding: 0 var(--space-3);
    font-size: var(--text-body);
  }

  input[data-number] {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    text-align: right;
    -moz-appearance: textfield;
    appearance: textfield;
  }

  input[data-number]::-webkit-outer-spin-button,
  input[data-number]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .helper {
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .error {
    font-size: var(--text-sm);
    color: var(--color-danger);
  }
</style>